// src/pages/DataRoom.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Main from "./Main";
import { makeCompanyNav } from "./nav";
import "./css/dataroom.css";

import { API_BASE } from "../services/server";
import { useAuth } from "../services/AuthContext";
import useWorkspaceLifecycle from "../hooks/useWorkspaceLifecycle";
import {
  WorkspaceLockOverlay,
  WorkspaceCloseBanner,
} from "./components/WorkspaceLifecycleUI";

/* ─────────────────────────────────────────────────────────
   API helpers
───────────────────────────────────────────────────────── */

import { getCsrfToken } from "../services/server";

async function apiFetch(path, { method = "GET", body, isForm = false } = {}) {
  const headers = {};
  const token = getCsrfToken();
  if (token) headers["X-CSRFToken"] = token;
  if (!isForm && body) headers["Content-Type"] = "application/json";
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: "include",
    headers,
    body: isForm ? body : body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return null;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.detail || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

function wsBase(slug) {
  return `/users/workspaces/${encodeURIComponent(slug)}/dataroom`;
}

/* ─────────────────────────────────────────────────────────
   Utility helpers
───────────────────────────────────────────────────────── */

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function fileIcon(filename) {
  const ext = (filename || "").split(".").pop().toLowerCase();
  const map = {
    pdf: "📄",
    doc: "📝", docx: "📝",
    xls: "📊", xlsx: "📊",
    ppt: "📉", pptx: "📉",
    jpg: "🖼", jpeg: "🖼", png: "🖼", gif: "🖼", svg: "🖼",
    zip: "🗜", rar: "🗜", "7z": "🗜",
    txt: "📃",
    csv: "📊",
    msg: "✉️",
  };
  return map[ext] || "📎";
}

/* ─────────────────────────────────────────────────────────
   Flatten the tree into a lookup
───────────────────────────────────────────────────────── */

function buildFolderMap(tree) {
  const map = {};
  function walk(node, depth = 0) {
    map[node.id] = { ...node, depth };
    (node.folders || []).forEach((ch) => walk(ch, depth + 1));
  }
  tree.forEach((r) => walk(r, 0));
  return map;
}

function allFilesFlat(folderMap, selectedFolderId) {
  if (selectedFolderId === null) {
    // root: show all root-level folder contents
    return [];
  }
  const folder = folderMap[selectedFolderId];
  if (!folder) return [];
  return folder.files || [];
}

/* ─────────────────────────────────────────────────────────
   Folder tree sidebar node
───────────────────────────────────────────────────────── */

function FolderNode({ node, selectedId, onSelect, depth = 0 }) {
  const [open, setOpen] = useState(depth === 0);
  const hasChildren = node.folders && node.folders.length > 0;
  const isSelected = selectedId === node.id;

  return (
    <div className="dr-tree-node">
      <div
        className={`dr-tree-row${isSelected ? " dr-tree-row--active" : ""}`}
        style={{ paddingLeft: `${12 + depth * 16}px` }}
        onClick={() => {
          onSelect(node.id);
          if (hasChildren) setOpen((v) => !v);
        }}
      >
        <span className="dr-tree-caret" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}>
          {hasChildren ? (open ? "▾" : "▸") : " "}
        </span>
        <span className="dr-tree-icon">📁</span>
        <span className="dr-tree-label">{node.name}</span>
        {node.files && node.files.length > 0 && (
          <span className="dr-tree-count">{node.files.length}</span>
        )}
      </div>
      {open && hasChildren && (
        <div className="dr-tree-children">
          {node.folders.map((ch) => (
            <FolderNode key={ch.id} node={ch} selectedId={selectedId} onSelect={onSelect} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   AI Search Panel
───────────────────────────────────────────────────────── */

// scope: null = parent workspace only, "all" = whole group, orgnr string = specific subsidiary
function DataRoomChatPanel({ slug, scope, onClose }) {
  const [messages, setMessages] = useState([]); // { role, content, sources? }
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput("");
    setError("");

    const userMsg = { role: "user", content: msg };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setLoading(true);

    const history = messages.map((m) => ({ role: m.role, content: m.content }));

    // Zero-bleed scoping: use koncern-chat endpoint for any group-level scope
    const useKoncern = scope !== null;
    const endpoint = useKoncern
      ? `/users/workspaces/${encodeURIComponent(slug)}/dataroom/koncern-chat/`
      : `${wsBase(slug)}/chat/`;
    const body = { message: msg, history };
    if (useKoncern) body.scope_orgnr = scope === "all" ? null : scope;

    try {
      const data = await apiFetch(endpoint, { method: "POST", body });
      setMessages([...nextMessages, { role: "assistant", content: data.answer, sources: data.sources || [] }]);
    } catch (e) {
      setError(e.message);
      setMessages(nextMessages);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dr-chat-panel">
      <div className="dr-chat-header">
        <div className="dr-chat-title">
          <span className="dr-ai-icon">✦</span>
          Datarum-chat
        </div>
        <button className="dr-ai-close" onClick={onClose}>✕</button>
      </div>

      <div className="dr-chat-messages">
        {messages.length === 0 && (
          <div className="dr-chat-hint">
            {scope === "all"
              ? "Ställer frågor mot alla bolag i koncernen. Inga dokument utanför gruppen kan läcka in."
              : scope
              ? `Söker endast i dokument för valt bolag (${scope}). Noll läckage till övriga bolag.`
              : "Ställ frågor om dokumenten i datarummet. AI:n läser innehållet och svarar på svenska."}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`dr-chat-msg dr-chat-msg--${m.role}`}>
            <div className="dr-chat-bubble">{m.content}</div>
            {m.sources && m.sources.length > 0 && (
              <div className="dr-chat-sources">
                Källdokument:{" "}
                {m.sources.map((s, si) => (
                  <span key={s.id} className="dr-chat-source-tag">
                    {s.name}{si < m.sources.length - 1 ? ", " : ""}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="dr-chat-msg dr-chat-msg--assistant">
            <div className="dr-chat-bubble dr-chat-bubble--loading">
              <span className="dr-chat-dots"><span /><span /><span /></span>
            </div>
          </div>
        )}
        {error && <div className="dr-ai-error">{error}</div>}
        <div ref={bottomRef} />
      </div>

      <div className="dr-chat-input-row">
        <input
          ref={inputRef}
          className="dr-chat-input"
          placeholder="Fråga om dokumenten…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
          disabled={loading}
        />
        <button className="dr-chat-send" onClick={send} disabled={loading || !input.trim()}>
          ↑
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Q&A panel — role-aware questions list
───────────────────────────────────────────────────────── */

export function QuestionsPanel({ slug, viewerRole, onClose, embedded = false }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newQ, setNewQ] = useState("");
  const [newQSubject, setNewQSubject] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [answerDraft, setAnswerDraft] = useState({}); // { qid: string }
  const [busyId, setBusyId] = useState(null);

  const canAsk = viewerRole === "buyer";
  const canAnswer = viewerRole === "broker" || viewerRole === "seller";

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiFetch(`/users/workspaces/${encodeURIComponent(slug)}/questions/`);
      setQuestions(Array.isArray(data?.questions) ? data.questions : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  async function submitQuestion() {
    const q = newQ.trim();
    if (!q || submitting) return;
    setSubmitting(true);
    try {
      await apiFetch(`/users/workspaces/${encodeURIComponent(slug)}/questions/`, {
        method: "POST",
        body: { question: q, subject: newQSubject.trim() },
      });
      setNewQ("");
      setNewQSubject("");
      await load();
    } catch (e) {
      alert(`Kunde inte skicka fråga: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function submitAnswer(qid) {
    const answer = (answerDraft[qid] || "").trim();
    if (!answer || busyId) return;
    setBusyId(qid);
    try {
      await apiFetch(`/users/workspaces/${encodeURIComponent(slug)}/questions/${qid}/`, {
        method: "PATCH",
        body: { answer },
      });
      setAnswerDraft((d) => ({ ...d, [qid]: "" }));
      await load();
    } catch (e) {
      alert(`Kunde inte spara svar: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  }

  async function setPublished(q) {
    try {
      await apiFetch(`/users/workspaces/${encodeURIComponent(slug)}/questions/${q.id}/`, {
        method: "PATCH",
        body: { is_published: !q.is_published },
      });
      await load();
    } catch (e) {
      alert(`Kunde inte ändra synlighet: ${e.message}`);
    }
  }

  // Side-panel mode uses the chat panel chrome (fixed right-side overlay with
  // a close button). Embedded mode drops that chrome so the same UI fills
  // a full page in the seller/buyer portal Q&A routes.
  const rootClass = embedded ? "dr-qa-embedded" : "dr-chat-panel";
  const rootStyle = embedded
    ? { background: "#fff", border: "1px solid #E7E5E4", borderRadius: 10, padding: 0, maxWidth: 900, margin: "0 auto" }
    : undefined;
  return (
    <div className={rootClass} style={rootStyle}>
      {!embedded && (
        <div className="dr-chat-header">
          <div className="dr-chat-title">💬 Frågor &amp; Svar</div>
          <button className="dr-ai-close" onClick={onClose}>✕</button>
        </div>
      )}

      <div className={embedded ? "" : "dr-chat-messages"} style={{ padding: embedded ? 24 : 12 }}>
        {loading && <div className="dr-chat-hint">Laddar…</div>}
        {error && <div className="dr-chat-hint" style={{ color: "#b91c1c" }}>{error}</div>}

        {canAsk && !loading && (
          <div style={{ marginBottom: 18, padding: 12, background: "#F5F5F4", borderRadius: 8, border: "1px solid #E7E5E4" }}>
            <input
              type="text"
              placeholder="Ämne (valfritt)"
              value={newQSubject}
              onChange={(e) => setNewQSubject(e.target.value)}
              style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 8, border: "1px solid #E7E5E4", borderRadius: 6 }}
            />
            <textarea
              placeholder="Skriv din fråga här…"
              value={newQ}
              onChange={(e) => setNewQ(e.target.value)}
              rows={3}
              style={{ width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #E7E5E4", borderRadius: 6, resize: "vertical" }}
            />
            <button
              onClick={submitQuestion}
              disabled={submitting || !newQ.trim()}
              style={{ marginTop: 8, padding: "6px 14px", fontSize: 13, background: "#111827", color: "#fff", border: "none", borderRadius: 6, cursor: submitting ? "not-allowed" : "pointer" }}
            >
              {submitting ? "Skickar…" : "Skicka fråga"}
            </button>
          </div>
        )}

        {!loading && questions.length === 0 && (
          <div className="dr-chat-hint">
            {canAsk ? "Inga frågor ännu. Skriv en fråga ovan." : "Inga frågor har ställts ännu."}
          </div>
        )}

        {questions.map((q) => {
          const statusBadge = {
            open: { label: "Öppen", bg: "#FEF3C7", fg: "#78350F" },
            answered: { label: "Besvarad", bg: "#D1FAE5", fg: "#065F46" },
            closed: { label: "Stängd", bg: "#E5E7EB", fg: "#374151" },
          }[q.status] || { label: q.status, bg: "#E5E7EB", fg: "#374151" };

          return (
            <div key={q.id} style={{ marginBottom: 14, padding: 12, background: "#fff", border: "1px solid #E7E5E4", borderRadius: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                <div style={{ flex: 1 }}>
                  {q.subject && <div style={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>{q.subject}</div>}
                  {viewerRole !== "buyer" && (q.buyer_email || q.buyer_company) && (
                    <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                      Från: {q.buyer_company || q.buyer_email}
                    </div>
                  )}
                </div>
                <span style={{ padding: "2px 8px", fontSize: 11, borderRadius: 999, background: statusBadge.bg, color: statusBadge.fg, flexShrink: 0 }}>
                  {statusBadge.label}
                </span>
                {viewerRole === "broker" && (
                  <button
                    type="button"
                    onClick={() => setPublished(q)}
                    style={{ padding: "3px 8px", fontSize: 11, border: "1px solid #d1d5db", borderRadius: 5, background: q.is_published ? "#dcfce7" : "#fff", cursor: "pointer" }}
                  >
                    {q.is_published ? "Publicerad för köpare" : "Dela med köpare"}
                  </button>
                )}
              </div>
              <div style={{ fontSize: 13, color: "#374151", whiteSpace: "pre-wrap" }}>{q.question}</div>

              {q.answer && (
                <div style={{ marginTop: 10, padding: 10, background: "#F9FAFB", borderLeft: "3px solid #10B981", borderRadius: 4 }}>
                  <div style={{ fontSize: 11, color: "#059669", fontWeight: 600, marginBottom: 4 }}>SVAR</div>
                  <div style={{ fontSize: 13, color: "#111827", whiteSpace: "pre-wrap" }}>{q.answer}</div>
                </div>
              )}

              {canAnswer && q.status !== "closed" && (
                <div style={{ marginTop: 10 }}>
                  <textarea
                    placeholder={q.answer ? "Uppdatera svaret…" : "Skriv ett svar…"}
                    value={answerDraft[q.id] || ""}
                    onChange={(e) => setAnswerDraft((d) => ({ ...d, [q.id]: e.target.value }))}
                    rows={2}
                    style={{ width: "100%", padding: "6px 8px", fontSize: 12, border: "1px solid #E7E5E4", borderRadius: 6, resize: "vertical" }}
                  />
                  <button
                    onClick={() => submitAnswer(q.id)}
                    disabled={busyId === q.id || !(answerDraft[q.id] || "").trim()}
                    style={{ marginTop: 6, padding: "5px 12px", fontSize: 12, background: "#059669", color: "#fff", border: "none", borderRadius: 6, cursor: busyId === q.id ? "not-allowed" : "pointer" }}
                  >
                    {busyId === q.id ? "Sparar…" : q.answer ? "Uppdatera svar" : "Skicka svar"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   New Folder Dialog
───────────────────────────────────────────────────────── */

function NewFolderDialog({ onConfirm, onCancel }) {
  const [name, setName] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  return (
    <div className="dr-dialog-backdrop">
      <div className="dr-dialog">
        <div className="dr-dialog-title">Ny mapp</div>
        <input
          ref={inputRef}
          className="dr-dialog-input"
          placeholder="Mappnamn"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && name.trim()) onConfirm(name.trim());
            if (e.key === "Escape") onCancel();
          }}
        />
        <div className="dr-dialog-actions">
          <button className="dr-btn dr-btn--ghost" onClick={onCancel}>Avbryt</button>
          <button className="dr-btn dr-btn--primary" disabled={!name.trim()} onClick={() => onConfirm(name.trim())}>
            Skapa
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Rename Dialog
───────────────────────────────────────────────────────── */

function RenameDialog({ current, onConfirm, onCancel }) {
  const [name, setName] = useState(current || "");
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);

  return (
    <div className="dr-dialog-backdrop">
      <div className="dr-dialog">
        <div className="dr-dialog-title">Byt namn</div>
        <input
          ref={inputRef}
          className="dr-dialog-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && name.trim()) onConfirm(name.trim());
            if (e.key === "Escape") onCancel();
          }}
        />
        <div className="dr-dialog-actions">
          <button className="dr-btn dr-btn--ghost" onClick={onCancel}>Avbryt</button>
          <button className="dr-btn dr-btn--primary" disabled={!name.trim()} onClick={() => onConfirm(name.trim())}>
            Spara
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   File row context menu
───────────────────────────────────────────────────────── */

function FileContextMenu({
  x, y, doc, locked,
  canManage = true, canDelete = true, canRestrictDownload = false, buyerCanDownload = true,
  onRename, onMove, onNewVersion, onHistory, onDelete, onToggleRestrict, onClose,
}) {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const hasVersions = (doc.version_count || 0) > 1;
  const lockedTitle = locked ? "Datarummet är låst" : undefined;
  const showDownload = doc.file?.url && buyerCanDownload;

  return (
    <div ref={ref} className="dr-ctx-menu" style={{ top: y, left: x }}>
      {canManage && (
        <>
          <button className="dr-ctx-item" onClick={onRename} disabled={locked} title={lockedTitle}>Byt namn</button>
          <button className="dr-ctx-item" onClick={onMove} disabled={locked} title={lockedTitle}>Flytta</button>
          <button className="dr-ctx-item" onClick={onNewVersion} disabled={locked} title={lockedTitle}>Ladda upp ny version</button>
        </>
      )}
      <button className="dr-ctx-item" onClick={onHistory}>
        Versionshistorik{hasVersions ? ` (${doc.version_count})` : ""}
      </button>
      {showDownload && (
        <a className="dr-ctx-item dr-ctx-item--link" href={`${API_BASE}${doc.file.url}`} target="_blank" rel="noreferrer" download onClick={onClose}>
          Ladda ned
        </a>
      )}
      {canRestrictDownload && (
        <button className="dr-ctx-item" onClick={onToggleRestrict} disabled={locked} title={lockedTitle}>
          {doc.download_restricted ? "Tillåt nedladdning" : "Begränsa nedladdning"}
        </button>
      )}
      {canDelete && (
        <>
          <div className="dr-ctx-divider" />
          <button className="dr-ctx-item dr-ctx-item--danger" onClick={onDelete} disabled={locked} title={lockedTitle}>Ta bort</button>
        </>
      )}
    </div>
  );
}


/* ─────────────────────────────────────────────────────────
   Folder row context menu
───────────────────────────────────────────────────────── */

function FolderContextMenu({
  x, y, folder, locked,
  canManage = true, canDelete = true,
  onRename, onMove, onDelete, onClose,
}) {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const isDefault = !!folder.is_default;
  const disabled = isDefault || locked;
  const title = locked ? "Datarummet är låst" : isDefault ? "Systemstandardmapp" : undefined;

  return (
    <div ref={ref} className="dr-ctx-menu" style={{ top: y, left: x }}>
      {canManage && (
        <>
          <button className="dr-ctx-item" onClick={onRename} disabled={disabled} title={title}>Byt namn</button>
          <button className="dr-ctx-item" onClick={onMove} disabled={disabled} title={title}>Flytta</button>
        </>
      )}
      {canDelete && (
        <>
          {canManage && <div className="dr-ctx-divider" />}
          <button className="dr-ctx-item dr-ctx-item--danger" onClick={onDelete} disabled={disabled} title={title}>Ta bort</button>
        </>
      )}
    </div>
  );
}


/* ─────────────────────────────────────────────────────────
   Folder picker dialog — select a target folder
───────────────────────────────────────────────────────── */

function FolderPickerDialog({ tree, title, excludeId, onConfirm, onCancel }) {
  const [selected, setSelected] = useState(null); // null = root

  const renderNode = (node, depth = 0) => {
    const isExcluded = excludeId != null && node.id === excludeId;
    if (isExcluded) return null;
    return (
      <React.Fragment key={node.id}>
        <div
          className={`dr-picker-row${selected === node.id ? " dr-picker-row--selected" : ""}`}
          style={{ paddingLeft: 12 + depth * 16 }}
          onClick={() => setSelected(node.id)}
        >
          <span className="dr-file-icon">📁</span>
          <span>{node.name}</span>
        </div>
        {(node.folders || []).map((child) => renderNode(child, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className="dr-dialog-backdrop">
      <div className="dr-dialog dr-dialog--wide">
        <div className="dr-dialog-title">{title}</div>
        <div className="dr-picker-list">
          <div
            className={`dr-picker-row${selected === null ? " dr-picker-row--selected" : ""}`}
            onClick={() => setSelected(null)}
          >
            <span className="dr-file-icon">🏠</span>
            <span>Datarummets rot</span>
          </div>
          {(tree || []).map((node) => renderNode(node, 0))}
        </div>
        <div className="dr-dialog-actions">
          <button className="dr-btn dr-btn--ghost" onClick={onCancel}>Avbryt</button>
          <button className="dr-btn dr-btn--primary" onClick={() => onConfirm(selected)}>
            Flytta hit
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   New Version Dialog — pick replacement file + optional note
───────────────────────────────────────────────────────── */

function NewVersionDialog({ doc, onConfirm, onCancel, uploading }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");

  return (
    <div className="dr-dialog-backdrop">
      <div className="dr-dialog">
        <div className="dr-dialog-title">Ladda upp ny version</div>
        <p style={{ fontSize: 13, color: "#78716c", marginTop: 0, marginBottom: 12 }}>
          Ersätter den aktuella versionen av{" "}
          <strong style={{ color: "#1c1917" }}>
            {doc.title || doc.original_filename}
          </strong>
          . Tidigare versioner sparas i historiken.
        </p>

        <button
          type="button"
          className="dr-btn dr-btn--ghost"
          onClick={() => fileInputRef.current?.click()}
          style={{ width: "100%", justifyContent: "flex-start" }}
        >
          {file ? `✓ ${file.name}` : "Välj fil…"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) setFile(f);
            e.target.value = "";
          }}
        />

        <label style={{
          display: "block",
          marginTop: 14,
          fontSize: 12,
          fontWeight: 600,
          color: "#475569",
          marginBottom: 4,
        }}>
          Ändringsnotering (valfritt)
        </label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="t.ex. Uppdaterade bilaga 2"
          className="dr-dialog-input"
          maxLength={255}
        />

        <div className="dr-dialog-actions">
          <button className="dr-btn dr-btn--ghost" onClick={onCancel} disabled={uploading}>
            Avbryt
          </button>
          <button
            className="dr-btn dr-btn--primary"
            disabled={!file || uploading}
            onClick={() => onConfirm(file, note.trim())}
          >
            {uploading ? "Laddar upp…" : "Ladda upp"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Version History Drawer — right-side slide-in
───────────────────────────────────────────────────────── */

function VersionHistoryDrawer({ doc, versions, loading, error, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.35)",
          zIndex: 1000,
          animation: "drVerFade 0.2s ease-out",
        }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(560px, 100vw)",
          background: "#fff",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
          zIndex: 1001,
          display: "flex",
          flexDirection: "column",
          animation: "drVerSlide 0.25s ease-out",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            padding: "20px 24px",
            borderBottom: "1px solid #e7e5e4",
            flexShrink: 0,
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, color: "#78716c", marginBottom: 4, letterSpacing: 0.5 }}>
              VERSIONSHISTORIK
            </div>
            <h2
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: "#1c1917",
                margin: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={doc.title || doc.original_filename}
            >
              {doc.title || doc.original_filename}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Stäng"
            style={{
              background: "none",
              border: "none",
              fontSize: 22,
              color: "#78716c",
              cursor: "pointer",
              padding: "4px 8px",
              marginLeft: 12,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </header>
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
          {loading && <div style={{ color: "#78716c", fontSize: 13 }}>Laddar historik…</div>}
          {error && (
            <div style={{ color: "#dc2626", fontSize: 13, padding: "8px 12px", background: "#fef2f2", borderRadius: 6 }}>
              {error}
            </div>
          )}
          {!loading && !error && versions && versions.length === 0 && (
            <div style={{ color: "#78716c", fontSize: 13 }}>Inga versioner hittades.</div>
          )}
          {!loading && !error && versions && versions.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {versions.map((v) => (
                <div
                  key={v.version}
                  style={{
                    border: "1px solid #e7e5e4",
                    borderRadius: 8,
                    padding: "12px 14px",
                    background: v.is_current ? "#F5F5F4" : "#fff",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <strong style={{ fontSize: 14, color: "#1c1917" }}>v{v.version}</strong>
                    {v.is_current && (
                      <span style={{
                        fontSize: 11,
                        fontWeight: 600,
                        padding: "2px 8px",
                        background: "#57534E",
                        color: "#fff",
                        borderRadius: 10,
                      }}>
                        AKTUELL
                      </span>
                    )}
                    <span style={{ fontSize: 12, color: "#78716c", marginLeft: "auto" }}>
                      {v.uploaded_at ? new Date(v.uploaded_at).toLocaleString("sv-SE") : "—"}
                    </span>
                  </div>
                  {v.uploaded_by && (
                    <div style={{ fontSize: 12, color: "#57534E", marginBottom: 4 }}>
                      Uppladdad av: {v.uploaded_by}
                    </div>
                  )}
                  {v.change_note && (
                    <div style={{
                      fontSize: 13,
                      color: "#1c1917",
                      marginTop: 6,
                      padding: "6px 10px",
                      background: v.is_current ? "#fff" : "#FAFAF9",
                      borderRadius: 4,
                      borderLeft: "3px solid #A8A29E",
                    }}>
                      {v.change_note}
                    </div>
                  )}
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginTop: 8,
                    fontSize: 12,
                    color: "#78716c",
                  }}>
                    <span>{formatBytes(v.file_size)}</span>
                    {v.checksum && (
                      <span title={v.checksum} style={{ fontFamily: "monospace" }}>
                        {v.checksum.slice(0, 10)}…
                      </span>
                    )}
                    {v.file_url && (
                      <a
                        href={`${API_BASE}${v.file_url}`}
                        target="_blank"
                        rel="noreferrer"
                        download
                        style={{
                          marginLeft: "auto",
                          color: "#57534E",
                          fontWeight: 600,
                          textDecoration: "none",
                        }}
                      >
                        Ladda ned ↓
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>
      <style>{`
        @keyframes drVerFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes drVerSlide { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
    </>
  );
}

/* ─────────────────────────────────────────────────────────
   AI Chat button with upsell tooltip (broker-only)
───────────────────────────────────────────────────────── */

function NdaBanner({ workspaceKey, signingUrl, onSigningInitiated }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function handleInitiate() {
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      const data = await apiFetch(`/signing/${workspaceKey}/initiate/`, { method: "POST" });
      if (data?.already_signed) {
        window.location.reload();
        return;
      }
      if (data?.signing_url) {
        onSigningInitiated?.(data.signing_url);
        window.open(data.signing_url, "_blank", "noopener,noreferrer");
      } else {
        throw new Error("Inget signerings-URL returnerades.");
      }
    } catch (e) {
      setErr(e.message || "Kunde inte starta signering.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        margin: "16px",
        padding: "20px 24px",
        border: "1px solid #FCD34D",
        background: "#FEF3C7",
        borderRadius: 10,
        color: "#78350F",
        fontSize: 14,
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Sekretessavtal krävs</div>
      <div style={{ marginBottom: 12 }}>
        {signingUrl
          ? "Klicka på knappen nedan för att signera sekretessavtalet."
          : "Du måste signera sekretessavtalet innan du kan se dokumenten i datarummet. Tryck på knappen för att starta signeringen."}
      </div>
      {err && <div style={{ marginBottom: 10, color: "#9F1239", fontSize: 13 }}>{err}</div>}
      {signingUrl ? (
        <a
          href={signingUrl}
          target="_blank"
          rel="noreferrer"
          style={{
            display: "inline-block",
            padding: "8px 16px",
            background: "#92400E",
            color: "#fff",
            fontWeight: 600,
            borderRadius: 6,
            textDecoration: "none",
            fontSize: 13,
          }}
        >
          Signera nu →
        </a>
      ) : (
        <button
          type="button"
          onClick={handleInitiate}
          disabled={busy}
          style={{
            padding: "8px 16px",
            background: "#92400E",
            color: "#fff",
            fontWeight: 600,
            borderRadius: 6,
            border: "none",
            fontSize: 13,
            cursor: busy ? "wait" : "pointer",
          }}
        >
          {busy ? "Förbereder…" : "Starta signering →"}
        </button>
      )}
    </div>
  );
}


function AiChatButton({ active, enabled, onClick }) {
  const [hover, setHover] = useState(false);

  // Always shown as planned for now — feature is in roadmap, not enabled.
  return (
    <div style={{ position: "relative" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <button
        className="dr-btn dr-btn--ghost"
        style={{
          opacity: 0.55,
          cursor: "not-allowed",
          position: "relative",
          paddingRight: 26,
        }}
        onClick={(e) => e.preventDefault()}
        aria-disabled="true"
        title="Planerad funktion som kommer snart"
      >
        ✦ Chat
        <span style={{
          position: "absolute",
          right: 8,
          top: "50%",
          transform: "translateY(-50%)",
          fontSize: 10,
        }}>🔒</span>
      </button>

      {hover && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            minWidth: 240,
            maxWidth: 300,
            background: "#1C1917",
            color: "#fff",
            padding: "12px 14px",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
            zIndex: 100,
            fontSize: 13,
            lineHeight: 1.5,
            animation: "drUpsellFade 0.15s ease-out",
          }}
        >
          <div style={{ fontWeight: 600 }}>Planerad funktion som kommer snart</div>
          <div style={{
            position: "absolute",
            top: -5,
            right: 20,
            width: 10,
            height: 10,
            background: "#1C1917",
            transform: "rotate(45deg)",
          }} />
        </div>
      )}
      <style>{`@keyframes drUpsellFade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Main DataRoom component
───────────────────────────────────────────────────────── */

export default function DataRoom({ viewerRole, chromeless = false }) {
  const { company: slug } = useParams();
  const { user } = useAuth();
  const nav = user?.global_nav || {};
  const isBroker = !!nav.is_broker;
  const hasAiChat = !!nav.has_dataroom_ai_chat;

  // Role-aware rendering. Default inferred from global_nav for the
  // broker's own /dataroom/<slug>/ route. SellerPortal / BuyerPortal pass
  // an explicit prop ("seller" / "buyer") so the component hides admin
  // controls they aren't allowed to use.
  const effectiveRole = viewerRole || (isBroker ? "broker" : "buyer");
  const canUpload  = effectiveRole === "broker" || effectiveRole === "seller" || effectiveRole === "buyer";
  const canManage  = effectiveRole === "broker" || effectiveRole === "seller"; // create folder / rename / move / new version
  const canDelete  = effectiveRole === "broker";
  const canLock    = effectiveRole === "broker";
  const canUseChat = effectiveRole === "broker";

  const [tree, setTree] = useState([]);
  const [lockState, setLockState] = useState({ is_locked: false, locked_at: null, locked_reason: "" });
  const [unfiled, setUnfiled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ndaRequired, setNdaRequired] = useState(false);
  const [ndaSigningUrl, setNdaSigningUrl] = useState("");

  // Koncern scope: null = this workspace, "all" = whole group, orgnr = specific subsidiary
  const [scope, setScope] = useState(null);
  const [koncernInfo, setKoncernInfo] = useState(null); // { parent, subsidiaries }
  const [koncernTreeData, setKoncernTreeData] = useState(null); // for "all" mode

  const [selectedFolderId, setSelectedFolderId] = useState(null); // null = show root folders
  const [showAI, setShowAI] = useState(false);
  const [showQA, setShowQA] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [renameDoc, setRenameDoc] = useState(null); // { id, title }
  const [renameFolder, setRenameFolder] = useState(null); // { id, name }
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, doc }
  const [folderCtxMenu, setFolderCtxMenu] = useState(null); // { x, y, folder }
  const [movePicker, setMovePicker] = useState(null); // { kind: 'doc'|'folder', id, excludeId?, title }
  const [newVersionDoc, setNewVersionDoc] = useState(null); // { id, title, original_filename }
  const [newVersionUploading, setNewVersionUploading] = useState(false);
  const [historyDoc, setHistoryDoc] = useState(null); // { id, title, original_filename }
  const [historyVersions, setHistoryVersions] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");

  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // The active workspace slug for single-company operations
  const activeSlug = (scope === null || scope === "all") ? slug : scope;
  const isAllMode = scope === "all";
  const isReadOnly = isAllMode || lockState.is_locked; // upload/folder-create disabled in group view or when locked

  const folderMap = React.useMemo(() => buildFolderMap(tree), [tree]);

  /* ── Fetch koncern members (only if this workspace is a parent) ── */
  useEffect(() => {
    apiFetch(`/users/workspaces/${encodeURIComponent(slug)}/koncern/`)
      .then((data) => {
        if (data && data.subsidiaries && data.subsidiaries.length > 0) {
          setKoncernInfo(data);
        }
      })
      .catch(() => {});
  }, [slug]);

  /* ── Fetch tree ── */
  const loadTree = useCallback(async () => {
    setLoading(true);
    setError("");
    setNdaRequired(false);
    try {
      if (isAllMode) {
        const data = await apiFetch(`/users/workspaces/${encodeURIComponent(slug)}/dataroom/koncern-tree/`);
        setKoncernTreeData(data);
        // Build combined tree from all companies (each company's folders as top-level nodes)
        const allFolders = (data.companies || []).flatMap((c) => c.tree || []);
        const allUnfiled = (data.companies || []).flatMap((c) => c.unfiled || []);
        setTree(allFolders);
        setUnfiled(allUnfiled);
        setLockState({ is_locked: false, locked_at: null, locked_reason: "" });
      } else {
        const data = await apiFetch(`${wsBase(activeSlug)}/tree/`);
        setTree(data.tree || []);
        setUnfiled(data.unfiled || []);
        setKoncernTreeData(null);
        setLockState({
          is_locked: !!data.is_locked,
          locked_at: data.locked_at || null,
          locked_reason: data.locked_reason || "",
        });
      }
    } catch (e) {
      // Backend returns { nda_required: true } alongside 403 when a buyer
      // hasn't signed the workspace NDA. Surface that as its own state so
      // we can render a proper "sign NDA" screen instead of a raw error.
      // If a SigningDocument exists for this buyer, the response includes
      // a signing_url — we expose it as a one-click button.
      if (e.status === 403 && e.body?.nda_required) {
        setNdaRequired(true);
        setNdaSigningUrl(e.body?.signing_url || "");
      } else {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [slug, activeSlug, isAllMode]);

  useEffect(() => {
    setSelectedFolderId(null);
    loadTree();
  }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadTree(); }, [loadTree]);

  /* ── Breadcrumb ── */
  function getBreadcrumb() {
    if (selectedFolderId === null) return [];
    const path = [];
    let f = folderMap[selectedFolderId];
    while (f) {
      path.unshift({ id: f.id, name: f.name });
      f = f.parent_id ? folderMap[f.parent_id] : null;
    }
    return path;
  }

  /* ── Current files to display ── */
  const currentFiles = selectedFolderId === null
    ? unfiled
    : (folderMap[selectedFolderId]?.files || []);

  const currentSubfolders = selectedFolderId === null
    ? tree
    : (folderMap[selectedFolderId]?.folders || []);

  /* ── Upload ── */
  const uploadFiles = useCallback(async (files, folderId) => {
    if (!files || files.length === 0) return;
    if (isReadOnly) { alert("Välj ett specifikt bolag för att ladda upp filer."); return; }
    setUploading(true);
    try {
      const form = new FormData();
      Array.from(files).forEach((f) => form.append("files", f));
      if (folderId) form.append("folder_id", folderId);
      await apiFetch(`${wsBase(activeSlug)}/upload/`, { method: "POST", body: form, isForm: true });
      await loadTree();
    } catch (e) {
      alert(`Uppladdning misslyckades: ${e.message}`);
    } finally {
      setUploading(false);
    }
  }, [activeSlug, isReadOnly, loadTree]);

  const handleFileInput = (e) => {
    uploadFiles(e.target.files, selectedFolderId);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files, selectedFolderId);
  };

  /* ── New folder ── */
  const createFolder = async (name) => {
    setShowNewFolder(false);
    try {
      await apiFetch(`${wsBase(activeSlug)}/folders/`, {
        method: "POST",
        body: { name, parent_id: selectedFolderId ?? null },
      });
      await loadTree();
    } catch (e) {
      alert(`Kunde inte skapa mapp: ${e.message}`);
    }
  };

  /* ── Rename document ── */
  const renameDocument = async (newTitle) => {
    const doc = renameDoc;
    setRenameDoc(null);
    setCtxMenu(null);
    try {
      await apiFetch(`${wsBase(activeSlug)}/documents/${doc.id}/rename/`, {
        method: "PATCH",
        body: { title: newTitle },
      });
      await loadTree();
    } catch (e) {
      alert(`Kunde inte byta namn: ${e.message}`);
    }
  };

  /* ── Toggle download restriction (broker-only) ── */
  const toggleDownloadRestrict = async (doc) => {
    setCtxMenu(null);
    try {
      await apiFetch(`${wsBase(activeSlug)}/documents/${doc.id}/rename/`, {
        method: "PATCH",
        body: { download_restricted: !doc.download_restricted },
      });
      await loadTree();
    } catch (e) {
      alert(`Kunde inte uppdatera nedladdningsrättigheter: ${e.message}`);
    }
  };

  /* ── Rename folder ── */
  const renameFolderFn = async (newName) => {
    const f = renameFolder;
    setRenameFolder(null);
    setFolderCtxMenu(null);
    try {
      await apiFetch(`${wsBase(activeSlug)}/folders/${f.id}/rename/`, {
        method: "PATCH",
        body: { name: newName },
      });
      await loadTree();
    } catch (e) {
      alert(`Kunde inte byta namn: ${e.message}`);
    }
  };

  /* ── Move (document or folder) ── */
  const confirmMove = async (targetFolderId) => {
    const picker = movePicker;
    setMovePicker(null);
    if (!picker) return;
    try {
      if (picker.kind === "doc") {
        await apiFetch(`${wsBase(activeSlug)}/documents/${picker.id}/move/`, {
          method: "PATCH",
          body: { folder_id: targetFolderId },
        });
      } else {
        await apiFetch(`${wsBase(activeSlug)}/folders/${picker.id}/move/`, {
          method: "PATCH",
          body: { parent_id: targetFolderId },
        });
      }
      await loadTree();
    } catch (e) {
      alert(`Kunde inte flytta: ${e.message}`);
    }
  };

  /* ── Upload new version of an existing document ── */
  const uploadNewVersion = useCallback(async (file, changeNote) => {
    if (!newVersionDoc || !file) return;
    setNewVersionUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("replace_document_id", String(newVersionDoc.id));
      if (changeNote) form.append("change_note", changeNote);
      await apiFetch(`${wsBase(activeSlug)}/upload/`, {
        method: "POST",
        body: form,
        isForm: true,
      });
      setNewVersionDoc(null);
      await loadTree();
    } catch (e) {
      alert(`Kunde inte ladda upp ny version: ${e.message}`);
    } finally {
      setNewVersionUploading(false);
    }
  }, [newVersionDoc, activeSlug, loadTree]);

  /* ── Lock / unlock the data room ── */
  const lockDataRoom = useCallback(async () => {
    const reason = window.prompt(
      "Lås datarummet? Inga ändringar kan göras efteråt förutom av en mäklaradministratör som låser upp igen.\n\nValfri anledning:",
      ""
    );
    if (reason === null) return; // cancelled
    try {
      await apiFetch(`${wsBase(activeSlug)}/lock/`, {
        method: "POST",
        body: { reason: reason || "" },
      });
      await loadTree();
    } catch (e) {
      alert(`Kunde inte låsa datarummet: ${e.message}`);
    }
  }, [activeSlug, loadTree]);

  const unlockDataRoom = useCallback(async () => {
    if (!window.confirm(
      "Lås upp datarummet?\n\nUppladdningar, raderingar och ändringar kommer att tillåtas igen."
    )) return;
    try {
      await apiFetch(`${wsBase(activeSlug)}/lock/`, { method: "DELETE" });
      await loadTree();
    } catch (e) {
      alert(`Kunde inte låsa upp: ${e.message}`);
    }
  }, [activeSlug, loadTree]);

  /* ── Load version history for a document ── */
  const openVersionHistory = useCallback(async (doc) => {
    setHistoryDoc(doc);
    setHistoryVersions(null);
    setHistoryError("");
    setHistoryLoading(true);
    try {
      const data = await apiFetch(
        `${wsBase(activeSlug)}/documents/${doc.id}/versions/`
      );
      setHistoryVersions(data?.versions || []);
    } catch (e) {
      setHistoryError(e.message || "Kunde inte hämta historik.");
    } finally {
      setHistoryLoading(false);
    }
  }, [activeSlug]);

  /* ── Delete document ── */
  const deleteDocument = async (doc) => {
    setCtxMenu(null);
    if (!window.confirm(`Ta bort "${doc.title || doc.original_filename}"?`)) return;
    try {
      await apiFetch(`${wsBase(activeSlug)}/documents/${doc.id}/`, { method: "DELETE" });
      await loadTree();
    } catch (e) {
      alert(`Kunde inte ta bort: ${e.message}`);
    }
  };

  /* ── Delete folder ── */
  const deleteFolder = async (folder) => {
    if (!window.confirm(`Ta bort mappen "${folder.name}" och allt innehåll?`)) return;
    try {
      await apiFetch(`${wsBase(activeSlug)}/folders/${folder.id}/`, { method: "DELETE" });
      if (selectedFolderId === folder.id) setSelectedFolderId(null);
      await loadTree();
    } catch (e) {
      alert(`Kunde inte ta bort mapp: ${e.message}`);
    }
  };

  const breadcrumb = getBreadcrumb();

  /* ── Render ── */
  const content = (
      <div
        className={`dr-root${dragOver ? " dr-root--dragover" : ""}`}
        onDragOver={(e) => { if (!isReadOnly) { e.preventDefault(); setDragOver(true); } }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        {/* Sidebar */}
        <aside className="dr-sidebar">
          <div className="dr-sidebar-header">Datarum</div>

          {/* Scope picker — only shown for koncern parent workspaces */}
          {koncernInfo && (
            <div style={{ padding: "8px 12px", borderBottom: "1px solid var(--dr-border-light)" }}>
              <select
                value={scope ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setScope(val === "" ? null : val);
                }}
                style={{
                  width: "100%",
                  fontSize: "12px",
                  padding: "5px 8px",
                  border: "1px solid var(--dr-border)",
                  borderRadius: "var(--dr-radius-sm)",
                  background: "var(--dr-surface)",
                  color: "var(--dr-text-primary)",
                  cursor: "pointer",
                }}
              >
                <option value="">{koncernInfo.parent.name || "Moderbolag"}</option>
                <option value="all">Hela koncernen</option>
                {koncernInfo.subsidiaries.map((s) => (
                  <option key={s.orgnr} value={s.orgnr}>{s.name || s.orgnr}</option>
                ))}
              </select>
            </div>
          )}

          {loading ? (
            <div className="dr-sidebar-loading">Laddar…</div>
          ) : isAllMode && koncernTreeData ? (
            <nav className="dr-tree" style={{ overflowY: "auto", flex: 1 }}>
              <div
                className={`dr-tree-row dr-tree-row--root${selectedFolderId === null ? " dr-tree-row--active" : ""}`}
                onClick={() => setSelectedFolderId(null)}
              >
                <span className="dr-tree-icon">🏢</span>
                <span className="dr-tree-label">Hela koncernen</span>
              </div>
              {(koncernTreeData.companies || []).map((company) => (
                <div key={company.orgnr}>
                  <div style={{
                    padding: "6px 12px 4px",
                    fontSize: "10px",
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "var(--dr-text-muted)",
                    marginTop: "4px",
                  }}>
                    {company.company_name}
                  </div>
                  {(company.tree || []).map((node) => (
                    <FolderNode
                      key={node.id}
                      node={node}
                      selectedId={selectedFolderId}
                      onSelect={setSelectedFolderId}
                      depth={0}
                    />
                  ))}
                </div>
              ))}
            </nav>
          ) : (
            <nav className="dr-tree">
              {/* Root entry */}
              <div
                className={`dr-tree-row dr-tree-row--root${selectedFolderId === null ? " dr-tree-row--active" : ""}`}
                onClick={() => setSelectedFolderId(null)}
              >
                <span className="dr-tree-icon">🏠</span>
                <span className="dr-tree-label">Alla dokument</span>
              </div>
              {tree.map((node) => (
                <FolderNode
                  key={node.id}
                  node={node}
                  selectedId={selectedFolderId}
                  onSelect={setSelectedFolderId}
                  depth={0}
                />
              ))}
            </nav>
          )}
        </aside>

        {/* Main content */}
        <div className="dr-main">
          {/* Toolbar */}
          <div className="dr-toolbar">
            <div className="dr-breadcrumb">
              <span
                className="dr-bc-item dr-bc-link"
                onClick={() => setSelectedFolderId(null)}
              >
                Datarum
              </span>
              {breadcrumb.map((seg, i) => (
                <React.Fragment key={seg.id}>
                  <span className="dr-bc-sep">›</span>
                  <span
                    className={`dr-bc-item${i === breadcrumb.length - 1 ? " dr-bc-item--current" : " dr-bc-link"}`}
                    onClick={() => i < breadcrumb.length - 1 && setSelectedFolderId(seg.id)}
                  >
                    {seg.name}
                  </span>
                </React.Fragment>
              ))}
            </div>

            <div className="dr-toolbar-actions">
              {canUseChat && (
                <AiChatButton
                  active={showAI}
                  enabled={hasAiChat}
                  onClick={() => {
                    if (hasAiChat) setShowAI((v) => !v);
                  }}
                />
              )}
              <button
                className={`dr-btn dr-btn--ghost${showQA ? " dr-btn--active" : ""}`}
                onClick={() => setShowQA((v) => !v)}
                title="Frågor och svar"
              >
                💬 Q&amp;A
              </button>
              {canLock && !isAllMode && (
                lockState.is_locked ? (
                  <button
                    className="dr-btn dr-btn--ghost"
                    onClick={unlockDataRoom}
                    title="Lås upp datarummet"
                  >
                    🔓 Lås upp
                  </button>
                ) : (
                  <button
                    className="dr-btn dr-btn--ghost"
                    onClick={lockDataRoom}
                    title="Lås datarummet — ingen kan ändra innehåll"
                  >
                    🔒 Lås
                  </button>
                )
              )}
              {canManage && !isReadOnly && (
                <button
                  className="dr-btn dr-btn--ghost"
                  onClick={() => setShowNewFolder(true)}
                  title="Ny mapp"
                >
                  + Ny mapp
                </button>
              )}
              {canUpload && (
                <button
                  className="dr-btn dr-btn--primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || isReadOnly}
                  title={
                    lockState.is_locked
                      ? "Datarummet är låst"
                      : isAllMode
                        ? "Välj ett specifikt bolag för att ladda upp"
                        : "Ladda upp filer"
                  }
                >
                  {uploading ? "Laddar upp…" : "↑ Ladda upp"}
                </button>
              )}
              {canUpload && (
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="dr-hidden-input"
                  onChange={handleFileInput}
                />
              )}
            </div>
          </div>

          {/* Locked banner */}
          {lockState.is_locked && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 14px",
                margin: "0 16px 12px 16px",
                background: "#FEF3C7",
                border: "1px solid #FCD34D",
                borderRadius: 8,
                fontSize: 13,
                color: "#78350F",
                lineHeight: 1.4,
              }}
            >
              <span style={{ fontSize: 16 }}>🔒</span>
              <div style={{ flex: 1 }}>
                <strong>Datarummet är låst.</strong>{" "}
                Uppladdningar, raderingar och ändringar är inaktiverade.
                {lockState.locked_at && (
                  <>
                    {" "}
                    Låstes{" "}
                    {new Date(lockState.locked_at).toLocaleDateString("sv-SE", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                    .
                  </>
                )}
                {lockState.locked_reason && (
                  <div style={{ marginTop: 4, fontSize: 12, color: "#92400e" }}>
                    Anledning: {lockState.locked_reason}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Drop hint */}
          {dragOver && (
            <div className="dr-drop-hint">Släpp filer här för att ladda upp</div>
          )}

          {/* Error */}
          {error && <div className="dr-error-banner">{error}</div>}

          {/* NDA required — buyer cannot see the tree until they've signed. */}
          {ndaRequired && (
            <NdaBanner
              workspaceKey={activeSlug}
              signingUrl={ndaSigningUrl}
              onSigningInitiated={(url) => setNdaSigningUrl(url)}
            />
          )}

          {/* File table */}
          {!loading && (
            <div className="dr-table-wrapper">
              <table className="dr-table">
                <thead>
                  <tr>
                    <th className="dr-th dr-th--name">Namn</th>
                    <th className="dr-th dr-th--date">Datum</th>
                    <th className="dr-th dr-th--size">Storlek</th>
                    <th className="dr-th dr-th--ddq">DD-ref</th>
                    {isBroker && (
                      <th
                        className="dr-th dr-th--access"
                        title="Vem som kan se eller ladda ned filen. Endast synligt för mäklare."
                      >
                        Åtkomst
                      </th>
                    )}
                    <th className="dr-th dr-th--actions" />
                  </tr>
                </thead>
                <tbody>
                  {/* Sub-folders */}
                  {currentSubfolders.map((folder) => (
                    <tr
                      key={`f-${folder.id}`}
                      className="dr-tr dr-tr--folder"
                      onDoubleClick={() => setSelectedFolderId(folder.id)}
                    >
                      <td className="dr-td dr-td--name">
                        <div className="dr-name-inner">
                          <span className="dr-file-icon">📁</span>
                          <span
                            className="dr-file-name dr-file-name--folder"
                            onClick={() => setSelectedFolderId(folder.id)}
                          >
                            {folder.name}
                          </span>
                          <span className="dr-folder-meta">
                            {folder.files?.length || 0} fil{folder.files?.length !== 1 ? "er" : ""}
                            {folder.folders?.length > 0 && `, ${folder.folders.length} mappar`}
                          </span>
                        </div>
                      </td>
                      <td className="dr-td" />
                      <td className="dr-td" />
                      <td className="dr-td" />
                      {isBroker && <td className="dr-td" />}
                      <td className="dr-td dr-td--actions">
                        {!folder.is_default && (canManage || canDelete) && (
                          <button
                            className="dr-action-btn dr-action-btn--menu"
                            title="Fler alternativ"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              setFolderCtxMenu({ x: rect.right - 160, y: rect.bottom + 4, folder });
                            }}
                          >
                            ···
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}

                  {/* Files */}
                  {currentFiles.map((doc) => (
                    <tr key={`d-${doc.id}`} className="dr-tr dr-tr--file">
                      <td className="dr-td dr-td--name">
                        <div className="dr-name-inner">
                          <span className="dr-file-icon">
                            {fileIcon(doc.original_filename || doc.title)}
                          </span>
                          <span className="dr-file-name">{doc.title || doc.original_filename}</span>
                          {doc.original_filename && doc.title && doc.title !== doc.original_filename && (
                            <span className="dr-file-orig">{doc.original_filename}</span>
                          )}
                        </div>
                      </td>
                      <td className="dr-td dr-td--date">{formatDate(doc.created_at)}</td>
                      <td className="dr-td dr-td--size">{formatBytes(doc.file?.size)}</td>
                      <td className="dr-td dr-td--ddq">
                        {doc.ddq_qid && <span className="dr-ddq-badge">{doc.ddq_qid}</span>}
                      </td>
                      {isBroker && (
                        <td
                          className="dr-td dr-td--access"
                          title={
                            doc.download_restricted
                              ? "Synlig för mäklare och säljare. Köpare kan se filen i listan men kan inte ladda ned."
                              : "Synlig och nedladdningsbar för alla med tillgång (mäklare, säljare, köpare)."
                          }
                        >
                          {doc.download_restricted ? (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#92400E" }}>
                              🔒 Begränsad <span style={{ color: "#78716C" }}>(ej köpare)</span>
                            </span>
                          ) : (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#3F6212" }}>
                              ● Alla
                            </span>
                          )}
                        </td>
                      )}
                      <td className="dr-td dr-td--actions">
                        {doc.file?.url && !(effectiveRole === "buyer" && (doc.download_restricted || doc.download_allowed === false)) && (
                          <a
                            className="dr-action-btn"
                            href={`${API_BASE}${doc.file.url}`}
                            target="_blank"
                            rel="noreferrer"
                            download
                            title="Ladda ned"
                          >
                            ↓
                          </a>
                        )}
                        {effectiveRole === "buyer" && (doc.download_restricted || doc.download_allowed === false) && (
                          <span
                            className="dr-action-btn"
                            title="Nedladdning begränsad av mäklare"
                            style={{ opacity: 0.5, cursor: "not-allowed" }}
                          >
                            🔒
                          </span>
                        )}
                        <button
                          className="dr-action-btn dr-action-btn--menu"
                          title="Fler alternativ"
                          onClick={(e) => {
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            setCtxMenu({ x: rect.right - 160, y: rect.bottom + 4, doc });
                          }}
                        >
                          ···
                        </button>
                      </td>
                    </tr>
                  ))}

                  {/* Empty state */}
                  {currentSubfolders.length === 0 && currentFiles.length === 0 && (
                    <tr>
                      <td colSpan={isBroker ? 6 : 5} className="dr-empty">
                        {selectedFolderId === null
                          ? "Datarummet är tomt. Ladda upp filer eller skapa mappar."
                          : "Mappen är tom. Dra och släpp filer eller klicka Ladda upp."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {loading && <div className="dr-loading">Laddar datarum…</div>}
        </div>

        {/* Dataroom chat panel */}
        {showAI && isBroker && hasAiChat && (
          <DataRoomChatPanel slug={slug} scope={scope} onClose={() => setShowAI(false)} />
        )}

        {/* Q&A panel */}
        {showQA && (
          <QuestionsPanel slug={slug} viewerRole={effectiveRole} onClose={() => setShowQA(false)} />
        )}

        {/* File context menu */}
        {ctxMenu && (
          <FileContextMenu
            x={ctxMenu.x}
            y={ctxMenu.y}
            doc={ctxMenu.doc}
            locked={lockState.is_locked}
            canManage={canManage}
            canDelete={canDelete}
            canRestrictDownload={effectiveRole === "broker"}
            buyerCanDownload={
              // Buyers are blocked from downloading restricted files.
              effectiveRole !== "buyer" || !ctxMenu.doc.download_restricted
            }
            onRename={() => { setRenameDoc({ id: ctxMenu.doc.id, title: ctxMenu.doc.title || ctxMenu.doc.original_filename }); setCtxMenu(null); }}
            onMove={() => { setMovePicker({ kind: "doc", id: ctxMenu.doc.id, title: `Flytta "${ctxMenu.doc.title || ctxMenu.doc.original_filename}"` }); setCtxMenu(null); }}
            onNewVersion={() => { setNewVersionDoc(ctxMenu.doc); setCtxMenu(null); }}
            onHistory={() => { openVersionHistory(ctxMenu.doc); setCtxMenu(null); }}
            onDelete={() => deleteDocument(ctxMenu.doc)}
            onToggleRestrict={() => toggleDownloadRestrict(ctxMenu.doc)}
            onClose={() => setCtxMenu(null)}
          />
        )}

        {/* Folder context menu */}
        {folderCtxMenu && (
          <FolderContextMenu
            x={folderCtxMenu.x}
            y={folderCtxMenu.y}
            folder={folderCtxMenu.folder}
            locked={lockState.is_locked}
            canManage={canManage}
            canDelete={canDelete}
            onRename={() => { setRenameFolder({ id: folderCtxMenu.folder.id, name: folderCtxMenu.folder.name }); setFolderCtxMenu(null); }}
            onMove={() => { setMovePicker({ kind: "folder", id: folderCtxMenu.folder.id, excludeId: folderCtxMenu.folder.id, title: `Flytta mappen "${folderCtxMenu.folder.name}"` }); setFolderCtxMenu(null); }}
            onDelete={() => { deleteFolder(folderCtxMenu.folder); setFolderCtxMenu(null); }}
            onClose={() => setFolderCtxMenu(null)}
          />
        )}

        {/* Dialogs */}
        {showNewFolder && (
          <NewFolderDialog onConfirm={createFolder} onCancel={() => setShowNewFolder(false)} />
        )}
        {renameDoc && (
          <RenameDialog
            current={renameDoc.title}
            onConfirm={renameDocument}
            onCancel={() => setRenameDoc(null)}
          />
        )}
        {renameFolder && (
          <RenameDialog
            current={renameFolder.name}
            onConfirm={renameFolderFn}
            onCancel={() => setRenameFolder(null)}
          />
        )}
        {movePicker && (
          <FolderPickerDialog
            tree={tree}
            title={movePicker.title}
            excludeId={movePicker.excludeId}
            onConfirm={confirmMove}
            onCancel={() => setMovePicker(null)}
          />
        )}
      </div>
  );

  if (chromeless) return content;
  return (
    <Main navItems={makeCompanyNav(slug)}>
      <DataroomLifecycleWrap slug={slug}>{content}</DataroomLifecycleWrap>
    </Main>
  );
}

function DataroomLifecycleWrap({ slug, children }) {
  const { workspace, reload } = useWorkspaceLifecycle(slug);
  return (
    <>
      <WorkspaceCloseBanner workspace={workspace} />
      <WorkspaceLockOverlay workspace={workspace} onActivate={reload}>
        {children}
      </WorkspaceLockOverlay>
    </>
  );
}
